import { Fragment, useCallback, useEffect, useState } from "react";
import { AddLink } from "../design/AddLink";
import { Link, useNavigate, useParams } from "react-router-dom";

import { may, send } from "../api/auth";
import {
  console_,
  type CriticalityLevel,
  type PayRuleSetOption,
  type RateBasis,
  type Requirement,
  type RequirementOverlap,
  type RoleDef,
} from "../api/console";
import { LoadFailed } from "../design/LoadFailed";
import { StatusChip } from "../design/StatusChip";
import { useT } from "../i18n/LocaleProvider";
import { translateLabel, translateRefusal } from "../i18n/translate";
import { BriefingEditor } from "./BriefingEditor";
import { RequirementEditor } from "./RequirementEditor";
import { useSession } from "../session/SessionProvider";
import type { Failure } from "../system/failure";
import { typedTimeHint } from "../system/clock";
import { clock, minorPerMajor, money, span, whenCompact } from "../system/format";
import { roleGroups } from "../system/roleGroups";
import { usePartialLoad } from "./PartialLoad";

// ---------------------------------------------------------------------------
// REQUIREMENTS — V11. THE ATOMIC UNIT OF DEMAND.
//
// Role x shift x headcount x criticality x zone x pay rules x charge rate. The
// row V13 assigns against, V17 scores, V22 proves and V23 prices.
//
// THE RULES THIS FILE FOLLOWS, EACH EASY TO BREAK HERE IN PARTICULAR:
//
//   1. NO CRITICALITY LEVEL, RATE BASIS OR EVENT STATE IS NAMED. Not in a
//      comparison, not in a lookup table, not in a colour decision. Neither the
//      word for level 5 nor the id of the hourly basis appears anywhere below —
//      including in this comment, which is not pedantry: verify-V11.sh §7 greps
//      the source for the vocabulary's own strings, and a comment SAYING the
//      word is absent while containing it is a guard defeated by the sentence
//      claiming it holds. The server sends `blocks_opening`,
//      `counts_toward_readiness` and `multiplies_by_hours`, which are the
//      questions the screen actually asks.
//   2. NO DEFAULT IS INVENTED FOR THE BREAK POLICY. `break_is_paid` has no
//      database default and no default here either. The radio pair starts with
//      neither option chosen and the form cannot be submitted until one is —
//      because a paid twenty minutes and an unpaid twenty minutes are different
//      money for identical work, and a guessed answer is wrong silently.
//   3. MONEY IS ENTERED IN MAJOR UNITS AND SENT IN MINOR ONES, converted once,
//      at the boundary, with the currency's own exponent. There is no float in
//      the payload.
//   4. THERE IS NO NOTES FIELD. A requirement is filled in while a coordinator
//      is on the phone to a client, which makes it the second most natural
//      place in the product for the sentence a client should never have said.
//      Absolute rule 5.
//   5. THE DESTRUCTIVE ACTION CONFIRMS IN-PAGE. `window.confirm` is suppressed
//      in automated browsers, so a confirmation that used it would be
//      unverifiable AND invisible — the button would appear to do nothing.
// ---------------------------------------------------------------------------

// A failed read keeps its status, so a limit and a fault read differently (design/LoadFailed).
type Loaded<T> = { state: "loading" } | { state: "error"; failure: Failure } | { state: "ready"; data: T };

type Payload = {
  event: { id: string; name: string; accepts_planning: boolean };
  requirements: Requirement[];
  overlaps: RequirementOverlap[];
};

/** A `datetime-local` value `hours` from now. */
function localAt(hours: number): string {
  const d = new Date(Date.now() + hours * 3600_000);
  d.setSeconds(0, 0);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// ===========================================================================
// The list
// ===========================================================================

export function RequirementList() {
  const { apiBase, me } = useSession();
  const { locale, t, refusal } = useT();
  const orgZone = me !== null && me.authenticated ? (me.active_org?.timezone ?? null) : null;
  const { id = "" } = useParams();
  const [loaded, setLoaded] = useState<Loaded<Payload>>({ state: "loading" });
  const [notice, setNotice] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [briefing, setBriefing] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [details, setDetails] = useState<string | null>(null);
  // S10. The charge rule sets a post could be pinned to, for the ones that have none.
  const [chargeSets, setChargeSets] = useState<PayRuleSetOption[]>([]);
  // S27b. The check-in policies of the event's pinned version, to change a post's.
  const [policies, setPolicies] = useState<{ id: string; name: string }[]>([]);
  const [busy, setBusy] = useState(false);

  const canManage = me ? may(me, "event.manage") : false;

  const load = useCallback(async () => {
    const { status, body } = await send<Payload & { error?: { message?: string } }>(
      console_.listRequirements(apiBase, id),
    );
    if (status === 200 && body.requirements) setLoaded({ state: "ready", data: body });
    else setLoaded({ state: "error", failure: { status, refusal: refusal(body.error) } });
  }, [apiBase, id]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!canManage) return;
    send<{ pay_rule_sets?: PayRuleSetOption[] }>(console_.listPayRuleSets(apiBase))
      .then((res) => setChargeSets((res.body.pay_rule_sets ?? []).filter((p) => p.is_pinnable && p.basis === "charge")))
      .catch(() => setChargeSets([]));
  }, [apiBase, canManage]);

  useEffect(() => {
    if (!canManage || !id) return;
    void (async () => {
      try {
        const ev = await send<{ event?: { config_pack_version_id: string } }>(console_.getEvent(apiBase, id));
        const versionId = ev.body.event?.config_pack_version_id;
        if (!versionId) return;
        const v = await send<{ checkin_policies?: { id: string; name: string }[] }>(console_.getConfigPackVersion(apiBase, versionId));
        setPolicies(v.body.checkin_policies ?? []);
      } catch {
        setPolicies([]);
      }
    })();
  }, [apiBase, canManage, id]);

  async function setCheckin(requirementId: string, policyId: string) {
    setNotice(null);
    try {
      const { status, body } = await send<{ error?: { message?: string } }>(
        console_.updateRequirement(apiBase, requirementId, { checkin_policy_id: policyId === "" ? null : policyId }),
      );
      if (status !== 200) {
        setNotice(refusal(body.error) ?? t("How people check in could not be changed."));
        return;
      }
      await load();
    } catch {
      setNotice(t("We could not reach Verbunk. Check your connection and try again."));
    }
  }

  async function pinCharge(requirementId: string, chargeRuleSetId: string) {
    setNotice(null);
    try {
      const { status, body } = await send<{ error?: { message?: string } }>(
        console_.updateRequirement(apiBase, requirementId, { charge_rule_set_id: chargeRuleSetId }),
      );
      if (status !== 200) {
        setNotice(refusal(body.error) ?? t("The charge rates could not be set."));
        return;
      }
      await load();
    } catch {
      setNotice(t("We could not reach Verbunk. Check your connection and try again."));
    }
  }

  async function remove(requirementId: string) {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    try {
      const { status, body } = await send<{ error?: { message?: string } }>(
        console_.removeRequirement(apiBase, requirementId),
      );
      if (status !== 200) {
        setNotice(refusal(body.error) ?? t("That requirement could not be removed."));
        return;
      }
      setConfirming(null);
      await load();
    } catch {
      setNotice(t("We could not reach Verbunk. Check your connection and try again."));
    } finally {
      setBusy(false);
    }
  }

  if (loaded.state === "loading") return <p className="muted">{t("Loading…")}</p>;

  if (loaded.state === "error") {
    return (
      <section className="panel">
        <div className="panel-head">
          <h2>{t("Requirements")}</h2>
        </div>
        <LoadFailed failure={loaded.failure} fallback={t("We could not load this event's requirements.")} onRetry={() => void load()} />
        <Link className="btn btn-quiet" to="/console/events">
          {t("Back to events")}
        </Link>
      </section>
    );
  }

  const { event, requirements, overlaps } = loaded.data;

  return (
    <div>
      <section className="panel">
        <div className="panel-head">
          <h2>{t("Requirements")} — {event.name}</h2>
          {canManage && event.accepts_planning ? (
            <AddLink to={`/console/events/${event.id}/requirements/new`} label={t("Add a requirement")} />
          ) : null}
          <div className="row">
            <Link className="btn btn-quiet" to={`/console/events/${event.id}`}>
              {t("Event workspace")}
            </Link>
          </div>
        </div>

        {notice ? (
          <div className="form-message" role="status" aria-live="polite">
            <StatusChip tone="attention" label={t("Not applied")} />
            <span>{notice}</span>
          </div>
        ) : null}

        {!event.accepts_planning ? (
          <p className="muted">
            {t("This event no longer accepts planning, so what was asked for is shown here as a record and cannot be changed.")}
          </p>
        ) : null}

        {requirements.length === 0 ? (
          <p className="muted">
            {t("Nothing has been asked for yet. A requirement is one role, at one post, for one shift — how many people, how badly they are needed, what they are paid against and what the client is charged.")}
          </p>
        ) : null}

        {requirements.length > 0 ? (
          <table className="data-table">
            <thead>
              <tr>
                <th scope="col">{t("Role")}</th>
                <th scope="col">{t("Post")}</th>
                <th scope="col">{t("Shift")}</th>
                <th scope="col">{t("People")}</th>
                <th scope="col">{t("How badly")}</th>
                <th scope="col">{t("Charged")}</th>
                {canManage ? <th scope="col">&nbsp;</th> : null}
              </tr>
            </thead>
            <tbody>
              {requirements.map((r) => (
                <Fragment key={r.id}>
                <tr>
                  <th scope="row">
                    {translateLabel(locale, r.role_name)}
                    {r.role_is_retired ? (
                      <>
                        {" "}
                        {/* A RETIRED ROLE STILL RESOLVES and the row still renders.
                            Hiding it would make an old requirement show a blank
                            where the demand used to be. */}
                        <StatusChip tone="neutral" label={t("Retired")} />
                      </>
                    ) : null}
                    {/* S110 (0229). The gate refuses everyone on this post until its
                        briefing carries the signed assessment's controls, and only a
                        scheduler can publish it — so it is said here, on the list. */}
                    {r.safety_controls_unpublished ? (
                      <>
                        {" "}
                        <StatusChip tone="attention" label={t("Safety controls not published")} />
                      </>
                    ) : null}
                  </th>
                  <td>{r.zone ?? "—"}</td>
                  <td className="nowrap">
                    {whenCompact(r.starts_at, orgZone)}–{clock(r.ends_at)} · {span(r.starts_at, r.ends_at)}
                  </td>
                  <td className="numeric">{r.headcount}</td>
                  <td>
                    {/* THE NUMBER AND THE WORD, and a chip only where the level
                        says the event cannot open without it. Criticality is an
                        attribute rather than a status, so it is not coloured —
                        but "the gates cannot open" is a thing needing a human,
                        which is what amber means. */}
                    {r.criticality} · {translateLabel(locale, r.criticality_label)}
                    {r.criticality_blocks_opening ? (
                      <>
                        {" "}
                        <StatusChip tone="attention" label={t("Blocks opening")} />
                      </>
                    ) : null}
                  </td>
                  <td className="numeric">
                    {money(r.charge_rate_minor, r.charge_currency)}
                  </td>
                  {canManage ? (
                    <td className="nowrap">
                      <button
                        className="btn btn-quiet"
                        type="button"
                        aria-expanded={details === r.id}
                        onClick={() => setDetails(details === r.id ? null : r.id)}
                      >
                        {t("Details")}
                      </button>
                    </td>
                  ) : null}
                </tr>
                {details === r.id ? (
                  <tr>
                    <td colSpan={canManage ? 7 : 6}>
                      <dl className="facts">
                        <div>
                          <dt>{t("Paid against")}</dt>
                          <dd>
                    {r.pay_rule_set_name}
                    <br />
                    <span className="muted">
                      {r.pay_config_pack_name} v{r.pay_config_pack_version}
                    </span>
                          </dd>
                        </div>
                        <div>
                          <dt>{t("Charge basis and check-in")}</dt>
                          <dd>
                            <span className="muted">{translateLabel(locale, r.charge_rate_basis_label)}</span>
                    <br />
                    {r.charge_rule_set_name ? (
                      <span className="muted">{t("Invoiced against {name}", { name: r.charge_rule_set_name })}</span>
                    ) : canManage && chargeSets.length > 0 ? (
                      <select
                        aria-label={t("Charge rates for {role}", { role: translateLabel(locale, r.role_name) })}
                        value=""
                        onChange={(e) => e.target.value && void pinCharge(r.id, e.target.value)}
                      >
                        <option value="">{t("Choose charge rates…")}</option>
                        {chargeSets.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <StatusChip tone="attention" label={t("No charge rates — cannot be invoiced")} />
                    )}
                    <br />
                    {canManage && event.accepts_planning && !r.checkin_policy_locked && policies.length > 0 ? (
                      <select
                        aria-label={t("How people check in for {role}", { role: translateLabel(locale, r.role_name) })}
                        value={r.checkin_policy_id ?? ""}
                        onChange={(e) => void setCheckin(r.id, e.target.value)}
                      >
                        <option value="">{t("No check-in — hours are stated")}</option>
                        {policies.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className="muted">
                        {r.checkin_policy_name
                          ? t("Check-in: {name}", { name: r.checkin_policy_name })
                          : t("No check-in — hours are stated")}
                      </span>
                    )}
                          </dd>
                        </div>
                        <div>
                          <dt>{t("Break")}</dt>
                          <dd>
                    {t("{n} min", { n: r.break_minutes })}
                    <br />
                    {/* PAID OR UNPAID IN WORDS, never as a tick. A tick with no
                        label is a boolean the reader has to guess the polarity
                        of, and the two answers are different money. */}
                    <span className="muted">{r.break_is_paid ? t("Paid") : t("Unpaid")}</span>
                          </dd>
                        </div>
                      </dl>
                      {canManage ? (
                        <div className="row">
                          {confirming === r.id ? (
                        <span className="row">
                          <button
                            className="btn"
                            type="button"
                            disabled={busy}
                            onClick={() => void remove(r.id)}
                          >
                            {busy ? t("Working…") : t("Yes, remove")}
                          </button>
                          <button className="btn btn-quiet" type="button" onClick={() => setConfirming(null)}>
                            {t("Keep")}
                          </button>
                        </span>
                      ) : (
                        <span className="row">
                          <button
                            className="btn btn-quiet"
                            type="button"
                            onClick={() => setBriefing(briefing === r.id ? null : r.id)}
                          >
                            {t("Briefing")}
                          </button>
                          <button
                            className="btn btn-quiet"
                            type="button"
                            disabled={!event.accepts_planning}
                            onClick={() => setEditing(editing === r.id ? null : r.id)}
                          >
                            {t("Change")}
                          </button>
                          <button
                            className="btn btn-quiet"
                            type="button"
                            disabled={!event.accepts_planning}
                            onClick={() => setConfirming(r.id)}
                          >
                            {t("Remove")}
                          </button>
                        </span>
                      )}
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ) : null}
                {editing === r.id ? (
                  <tr>
                    <td colSpan={canManage ? 7 : 6}>
                      <RequirementEditor
                        requirement={r}
                        onClose={() => setEditing(null)}
                        onSaved={() => {
                          setEditing(null);
                          void load();
                        }}
                      />
                    </td>
                  </tr>
                ) : null}
                {briefing === r.id ? (
                  <tr>
                    <td colSpan={canManage ? 7 : 6}>
                      <BriefingEditor requirementId={r.id} onClose={() => setBriefing(null)} />
                    </td>
                  </tr>
                ) : null}
                </Fragment>
              ))}
            </tbody>
          </table>
        ) : null}
      </section>

      {/* OVERLAPS ARE REPORTED, NOT PREVENTED, and the panel says which it might
          be. A relief crew arriving before the outgoing crew leaves is a
          handover; the system cannot tell that from a double-booking, and
          guessing would train a coordinator to ignore the panel. */}
      {overlaps.length > 0 ? (
        <section className="panel">
          <div className="panel-head">
            <h3>{t("Two requirements on one post")}</h3>
            <StatusChip tone="attention" label={t("{n} to look at", { n: overlaps.length })} />
          </div>
          <ul className="plain-list">
            {overlaps.map((o) => (
              <li key={o.requirement_ids.join("-")}>
                <StatusChip tone="attention" label={t("Overlap")} />{" "}
                {translateRefusal(locale, { message: o.sentence, sentence_key: o.sentence_key, params: o.params })}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

// ===========================================================================
// Create
// ===========================================================================

export function NewRequirement() {
  const { apiBase, me } = useSession();
  const { locale, t, refusal } = useT();
  const partial = usePartialLoad();
  // Whose clock the shift window below is typed in. See system/clock.ts.
  const orgZone = me !== null && me.authenticated ? (me.active_org?.timezone ?? null) : null;
  const { id: eventId = "" } = useParams();
  const navigate = useNavigate();

  const [roles, setRoles] = useState<RoleDef[]>([]);
  const [payRuleSets, setPayRuleSets] = useState<PayRuleSetOption[]>([]);
  const [levels, setLevels] = useState<CriticalityLevel[]>([]);
  const [bases, setBases] = useState<RateBasis[]>([]);

  const [roleId, setRoleId] = useState("");
  const [payRuleSetId, setPayRuleSetId] = useState("");
  const [startsAt, setStartsAt] = useState(localAt(24 * 30));
  const [endsAt, setEndsAt] = useState(localAt(24 * 30 + 8));
  const [headcount, setHeadcount] = useState("1");
  const [criticality, setCriticality] = useState("");
  const [zone, setZone] = useState("");
  const [chargeRate, setChargeRate] = useState("");
  // S20: the organisation's own currency, not a fixed GBP.
  const [chargeCurrency, setChargeCurrency] = useState(
    me !== null && me.authenticated ? (me.active_org?.currency ?? "GBP") : "GBP",
  );
  // SWEEP S27. HOW PEOPLE ON THIS POST CHECK IN — from the event's pinned version, the
  // first preselected. Without it presence was never recorded for any requirement.
  const [policies, setPolicies] = useState<{ id: string; name: string; method_label: string }[]>([]);
  const [checkinPolicyId, setCheckinPolicyId] = useState("");
  // SWEEP S10. What the client is charged against. Without it no shift on this post can be
  // priced for charge, and no invoice can be raised for it.
  const [chargeRuleSetId, setChargeRuleSetId] = useState("");
  const [chargeBasis, setChargeBasis] = useState("");
  const [breakMinutes, setBreakMinutes] = useState("0");
  // NEITHER, DELIBERATELY. There is no third value and no default: the form is
  // not submittable until somebody says which, which is the column's `not null
  // with no default` expressed as an interaction.
  const [breakIsPaid, setBreakIsPaid] = useState<"" | "paid" | "unpaid">("");

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void send<{ role_defs?: RoleDef[] }>(console_.listRoleDefs(apiBase)).then(({ status, body }) => {
      if (status === 200 && body.role_defs) setRoles(body.role_defs.filter((r) => !r.is_retired));
      else partial.note();
    }).catch(() => partial.note());
    void send<{ pay_rule_sets?: PayRuleSetOption[] }>(console_.listPayRuleSets(apiBase)).then(({ status, body }) => {
      if (status === 200 && body.pay_rule_sets) setPayRuleSets(body.pay_rule_sets);
      else partial.note();
    }).catch(() => partial.note());
    void send<{ criticality_levels?: CriticalityLevel[] }>(console_.listCriticalityLevels(apiBase)).then(
      ({ status, body }) => {
        if (status === 200 && body.criticality_levels) setLevels(body.criticality_levels);
        else partial.note();
      },
    ).catch(() => partial.note());
    void send<{ rate_bases?: RateBasis[] }>(console_.listRateBases(apiBase)).then(({ status, body }) => {
      if (status === 200 && body.rate_bases) setBases(body.rate_bases);
      else partial.note();
    }).catch(() => partial.note());
    void (async () => {
      try {
        const ev = await send<{ event?: { config_pack_version_id: string } }>(console_.getEvent(apiBase, eventId));
        const versionId = ev.body.event?.config_pack_version_id;
        if (!versionId) return;
        const v = await send<{ checkin_policies?: { id: string; name: string; method_label: string }[] }>(
          console_.getConfigPackVersion(apiBase, versionId),
        );
        const list = v.body.checkin_policies ?? [];
        setPolicies(list);
        if (list[0]) setCheckinPolicyId(list[0].id);
      } catch {
        // The select then offers only "hours are stated", which is a usable answer.
      }
    })();
  }, [apiBase, eventId]);

  const pinnable = payRuleSets.filter((p) => p.is_pinnable && p.basis !== "charge");
  const chargeable = payRuleSets.filter((p) => p.is_pinnable && p.basis === "charge");
  const blocked = payRuleSets.filter((p) => !p.is_pinnable);

  const rate = Number(chargeRate);
  const rateIsUsable = chargeRate.trim() !== "" && Number.isFinite(rate) && rate >= 0;
  const ready =
    roleId !== "" &&
    payRuleSetId !== "" &&
    criticality !== "" &&
    chargeBasis !== "" &&
    breakIsPaid !== "" &&
    rateIsUsable &&
    startsAt !== "" &&
    endsAt !== "";

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy || !ready) return;
    setBusy(true);
    setError(null);
    try {
      const { status, body } = await send<{ id?: string; error?: { message?: string } }>(
        console_.createRequirement(apiBase, eventId, {
          role_def_id: roleId,
          pay_rule_set_id: payRuleSetId,
          starts_at: new Date(startsAt).toISOString(),
          ends_at: new Date(endsAt).toISOString(),
          headcount: Number(headcount),
          criticality: Number(criticality),
          zone: zone.trim() === "" ? null : zone.trim(),
          // THE ONE CONVERSION, AT THE BOUNDARY, using the currency's own
          // exponent. Math.round rather than a truncation: 42.50 arrives as
          // 4249.999… in binary floating point often enough to matter, and a
          // truncated penny is a penny the client is not charged, per shift,
          // forever.
          charge_rate_minor: Math.round(rate * minorPerMajor(chargeCurrency)),
          charge_currency: chargeCurrency.trim().toUpperCase(),
          charge_rate_basis: chargeBasis,
          break_minutes: Number(breakMinutes),
          break_is_paid: breakIsPaid === "paid",
          checkin_policy_id: checkinPolicyId === "" ? null : checkinPolicyId,
          charge_rule_set_id: chargeRuleSetId === "" ? null : chargeRuleSetId,
        }),
      );
      if (status !== 201 || !body.id) {
        setError(refusal(body.error) ?? t("That did not work. Please try again."));
        return;
      }
      navigate(`/console/events/${eventId}/requirements`, { replace: true });
    } catch {
      setError(t("We could not reach Verbunk. Check your connection and try again."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel">
      {partial.notice}
      <div className="panel-head">
        <h2>{t("Add a requirement")}</h2>
      </div>

      <div className="form-message" role="status" aria-live="polite">
        {error ? (
          <>
            <StatusChip tone="failed" label={t("Not saved")} />
            <span>{error}</span>
          </>
        ) : null}
      </div>

      <form onSubmit={submit}>
        <div className="field">
          <label htmlFor="req-role">{t("Role")}</label>
          <select id="req-role" required value={roleId} onChange={(e) => setRoleId(e.target.value)}>
            <option value="">{t("Choose a role…")}</option>
            {/* SWEEP S28. Grouped by the pack that defined the role, the
                organisation's own before the platform templates: seven templates
                each define "Bar staff", and a flat list showed them as one name. */}
            {roleGroups(roles, t).map(([group, members]) => (
              <optgroup key={group} label={group}>
                {members.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          {roles.length === 0 ? (
            <p className="field-hint">
              {t("No roles could be loaded. A requirement is always for a defined role, so it cannot be created without one.")}
            </p>
          ) : (
            <p className="field-hint">{t("What the people are being asked to do. It cannot be changed afterwards.")}</p>
          )}
        </div>

        <div className="field">
          <label htmlFor="req-zone">{t("Post")}</label>
          <input
            id="req-zone"
            type="text"
            maxLength={80}
            value={zone}
            onChange={(e) => setZone(e.target.value)}
          />
          {/* A PLACE, AND THE HINT SAYS SO. The field is short and it is called
              a post, because a long box on a planning screen collects whatever
              the person typing is thinking about. */}
          <p className="field-hint">
            {t("Where they stand — “North Stand, Turnstile 14”. Two requirements on one post with overlapping shifts are reported so you can check they were meant.")}
          </p>
        </div>

        <div className="field">
          <label htmlFor="req-starts">{t("Shift starts")}</label>
          <input
            id="req-starts"
            type="datetime-local"
            required
            value={startsAt}
            onChange={(e) => setStartsAt(e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="req-ends">{t("Shift ends")}</label>
          <input
            id="req-ends"
            type="datetime-local"
            required
            value={endsAt}
            onChange={(e) => setEndsAt(e.target.value)}
          />
          <p className="field-hint">
            {t("A shift may start before the event opens and finish after it closes — build and strike crews do.")}
          </p>
          {/* ★ WHOSE CLOCK. Both fields above are datetime-local and carry no
              zone, so they are read in the BROWSER's. This is the window that
              decides when a person is told to turn up. See system/clock.ts. */}
          <p className="field-hint">{typedTimeHint(orgZone, t)}</p>
        </div>

        <div className="field">
          <label htmlFor="req-headcount">{t("How many people")}</label>
          <input
            id="req-headcount"
            type="number"
            min={1}
            max={10000}
            required
            value={headcount}
            onChange={(e) => setHeadcount(e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="req-criticality">{t("How badly it is needed")}</label>
          <select
            id="req-criticality"
            required
            value={criticality}
            onChange={(e) => setCriticality(e.target.value)}
          >
            <option value="">{t("Choose a level…")}</option>
            {levels.map((l) => (
              <option key={l.level} value={String(l.level)}>
                {l.level} · {translateLabel(locale, l.label)}
              </option>
            ))}
          </select>
          {levels.length === 0 ? (
            <p className="field-hint">
              {t("The criticality levels could not be read, so this requirement cannot be created. No level is invented here — the readiness score is computed from this answer.")}
            </p>
          ) : (
            <p className="field-hint">
              {/* THE DESCRIPTION COMES FROM THE VOCABULARY. Nothing here knows
                  what level 5 means. */}
              {(() => {
                const description = levels.find((l) => String(l.level) === criticality)?.description;
                return description
                  ? translateLabel(locale, description)
                  : t("There is no default. The readiness score, and whether a gap stops the gates opening, are computed from this.");
              })()}
            </p>
          )}
        </div>

        <div className="field">
          <label htmlFor="req-charge-rules">{t("Charged against")}</label>
          <select id="req-charge-rules" value={chargeRuleSetId} onChange={(e) => setChargeRuleSetId(e.target.value)}>
            <option value="">{t("Not chosen")}</option>
            {chargeable.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} — {p.config_pack_name} v{p.config_pack_version}
              </option>
            ))}
          </select>
          <p className="field-hint">
            {chargeable.length === 0
              ? t("No published charge rule set exists. Add one in Configuration — without it this post's hours cannot be invoiced.")
              : t("The rates the client is billed at. Without one, this post's hours cannot be invoiced.")}
          </p>
        </div>

        <div className="field">
          <label htmlFor="req-checkin">{t("How people check in")}</label>
          <select id="req-checkin" value={checkinPolicyId} onChange={(e) => setCheckinPolicyId(e.target.value)}>
            {policies.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} — {p.method_label}
              </option>
            ))}
            <option value="">{t("No check-in — hours are stated afterwards")}</option>
          </select>
          <p className="field-hint">
            {t("Without a check-in, nobody on this post can be checked in or out, the live board cannot say who is here, and hours have to be typed in after the shift.")}
          </p>
        </div>

        <div className="field">
          <label htmlFor="req-pay">{t("Paid against")}</label>
          <select id="req-pay" required value={payRuleSetId} onChange={(e) => setPayRuleSetId(e.target.value)}>
            <option value="">{t("Choose a pay rule set…")}</option>
            {pinnable.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} — {p.config_pack_name} v{p.config_pack_version}
                {p.is_platform ? t(" (template)") : ""}
              </option>
            ))}
          </select>
          {pinnable.length === 0 ? (
            <p className="field-hint">
              {t("No published pay rule set could be found, so this requirement cannot be created. Publish a configuration version first — a requirement pins pay so that what the work was worth cannot change afterwards.")}
            </p>
          ) : (
            <p className="field-hint">
              {t("Pinned per requirement, not per event: one event can put one role on one rate and another role on a different one. Pinned, not referenced — publishing an edit creates a new version and leaves this one alone.")}
            </p>
          )}
          {/* THE ONES THAT CANNOT BE PINNED ARE NAMED, WITH THE REASON. A rule
              set missing from a select with no explanation is the symptom that
              produces a support call rather than a fix. */}
          {blocked.length > 0 ? (
            <p className="field-hint">
              {t("Not available yet: {sets}.", {
                sets: blocked
                  .map((p) =>
                    t("{name} ({status} version)", {
                      name: p.name,
                      status: translateLabel(locale, p.config_pack_version_status_label).toLowerCase(),
                    }),
                  )
                  .join(", "),
              })}
            </p>
          ) : null}
        </div>

        <div className="field">
          <label htmlFor="req-charge">{t("Charged to the client")}</label>
          <input
            id="req-charge"
            type="number"
            min={0}
            step="0.01"
            required
            value={chargeRate}
            onChange={(e) => setChargeRate(e.target.value)}
          />
          <p className="field-hint">
            {t("What the client pays. Separate from what the worker is paid — renegotiating this never changes that.")}
          </p>
        </div>

        <div className="field">
          <label htmlFor="req-currency">{t("Currency")}</label>
          <input
            id="req-currency"
            type="text"
            maxLength={3}
            required
            value={chargeCurrency}
            onChange={(e) => setChargeCurrency(e.target.value.toUpperCase())}
          />
        </div>

        <div className="field">
          <label htmlFor="req-basis">{t("Charged per")}</label>
          <select id="req-basis" required value={chargeBasis} onChange={(e) => setChargeBasis(e.target.value)}>
            <option value="">{t("Choose a basis…")}</option>
            {bases.map((b) => (
              <option key={b.id} value={b.id}>
                {translateLabel(locale, b.label)}
              </option>
            ))}
          </select>
          <p className="field-hint">
            {(() => {
              const description = bases.find((b) => b.id === chargeBasis)?.description;
              return description
                ? translateLabel(locale, description)
                : t("A rate with no basis means one thing to an invoice and another to a margin report.");
            })()}
          </p>
        </div>

        <div className="field">
          <label htmlFor="req-break">{t("Break")}</label>
          <input
            id="req-break"
            type="number"
            min={0}
            max={1439}
            required
            value={breakMinutes}
            onChange={(e) => setBreakMinutes(e.target.value)}
          />
          <p className="field-hint">{t("Minutes. Enter 0 if there is none.")}</p>
        </div>

        <fieldset className="field">
          <legend>{t("Is the break paid?")}</legend>
          {/* NO DEFAULT. Both radios start unchecked and the submit button
              stays disabled until one is chosen — the column's `not null with
              no default` made visible. A pre-selected answer would be inherited
              rather than decided, and it is wrong in every jurisdiction where a
              short shift's break is paid. */}
          <label htmlFor="req-break-paid">
            <input
              id="req-break-paid"
              type="radio"
              name="break-paid"
              value="paid"
              checked={breakIsPaid === "paid"}
              onChange={() => setBreakIsPaid("paid")}
            />{" "}
            {t("Paid — the break counts as worked time")}
          </label>
          <label htmlFor="req-break-unpaid">
            <input
              id="req-break-unpaid"
              type="radio"
              name="break-paid"
              value="unpaid"
              checked={breakIsPaid === "unpaid"}
              onChange={() => setBreakIsPaid("unpaid")}
            />{" "}
            {t("Unpaid — the break is deducted")}
          </label>
          <p className="field-hint">{t("There is no default. A paid break and an unpaid one are different money.")}</p>
        </fieldset>

        <div className="row">
          <button className="btn" type="submit" disabled={busy || !ready}>
            {busy ? t("Working…") : t("Add requirement")}
          </button>
          <Link className="btn btn-quiet" to={`/console/events/${eventId}/requirements`}>
            {t("Cancel")}
          </Link>
        </div>
      </form>
    </section>
  );
}
